// ANN-6: Grid entity tool — two clicks place the extent; cols/rows from 50 mm default
// cells (or typed `cols,rows` before the second click).
import { type Vec } from "../core/geometry";
import { parseCount } from "../core/units";
import { newId, defaultTextStyle } from "../doc/document";
import type { GridEntity } from "../doc/entities";
import { renderGrid } from "../engine/renderAnnotation";
import type { RenderView } from "../engine/renderEntities";
import type { Tool, ToolCtx, ToolKeyEvent, ToolPointerEvent } from "./types";

const DEFAULT_CELL = 50; // mm

export class GridTool implements Tool {
  readonly id = "grid" as const;
  private anchor: Vec | null = null;
  private cursor: Vec | null = null;
  private fixedCounts: { cols: number; rows: number } | null = null;

  constructor(private ctx: ToolCtx) {}

  activate(): void {
    this.reset();
  }

  deactivate(): void {
    this.reset();
  }

  cancel(): void {
    this.reset();
  }

  inProgress(): boolean {
    return this.anchor !== null;
  }

  private reset(): void {
    this.anchor = null;
    this.cursor = null;
    this.fixedCounts = null;
    this.ctx.setSnapBase(null);
    this.ctx.setMeasure("pair", "cols,rows");
    this.ctx.setHint("Grid: click the first corner of the grid area");
    this.ctx.setCursorTip(null);
    this.ctx.requestRender();
  }

  pointerDown(e: ToolPointerEvent): void {
    if (e.button !== 0) return;
    if (!this.anchor) {
      this.anchor = e.point;
      this.ctx.setSnapBase(e.point);
      this.ctx.setHint("Grid: click the opposite corner — type cols,rows ⏎ to fix the counts");
      this.ctx.setCursorTip("opposite corner · cols,rows ⏎");
      return;
    }
    this.commit(e.point);
  }

  pointerMove(e: ToolPointerEvent): void {
    this.cursor = e.point;
    this.ctx.requestRender();
  }

  pointerUp(): void {}

  doubleClick(): void {}

  key(e: ToolKeyEvent): boolean {
    if (e.key === "Escape" && this.anchor) {
      this.reset();
      return true;
    }
    return false;
  }

  /** Typed `cols,rows` fixes the counts for the next placement (ANN-6). */
  measurement(raw: string): boolean {
    const parts = raw.split(",");
    if (parts.length !== 2) return false;
    const cols = parseCount(parts[0]!, 1, 1000);
    const rows = parseCount(parts[1]!, 1, 1000);
    if (cols === null || rows === null) return false;
    this.fixedCounts = { cols, rows };
    this.ctx.setHint(`Grid: ${cols}×${rows} — click the opposite corner`);
    return true;
  }

  private build(opposite: Vec): GridEntity | null {
    const a = this.anchor!;
    const x = Math.min(a.x, opposite.x);
    const y = Math.min(a.y, opposite.y);
    const w = Math.abs(opposite.x - a.x);
    const h = Math.abs(opposite.y - a.y);
    if (w < 1 || h < 1) return null;
    const cols = this.fixedCounts?.cols ?? Math.max(1, Math.round(w / DEFAULT_CELL));
    const rows = this.fixedCounts?.rows ?? Math.max(1, Math.round(h / DEFAULT_CELL));
    return {
      id: newId(),
      type: "grid",
      layerId: this.ctx.activeLayerId(),
      locked: false,
      hidden: false,
      pos: { x, y },
      rotationDeg: 0,
      cols,
      rows,
      cellW: w / cols,
      cellH: h / rows,
      lockAspect: true,
      labels: {
        cols: true,
        rows: true,
        colMode: "letters",
        rowMode: "numbers",
        textStyle: { ...defaultTextStyle(), size: 3, color: "#8a90a0" }
      },
      vStyle: { color: "#4dd0e1", width: 0.35, dash: "solid" },
      hStyle: { color: "#4dd0e1", width: 0.35, dash: "solid" },
      majorEvery: 0
    };
  }

  private commit(opposite: Vec): void {
    const entity = this.build(opposite);
    if (!entity) return; // zero-area: keep waiting
    this.ctx.store.dispatch({
      kind: "addEntities",
      entities: [entity],
      z: [{ layerId: entity.layerId, index: -1 }]
    });
    this.ctx.selection.set([entity.id]);
    this.ctx.switchTool("select");
  }

  renderDynamic(v: RenderView): void {
    if (!this.anchor || !this.cursor) return;
    const preview = this.build(this.cursor);
    if (preview) renderGrid(v, preview, undefined);
  }
}
